import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import DeleteIcon from "@mui/icons-material/Delete";
import { FieldArray } from "react-final-form-arrays";
import type { PlatformBlockComponent } from "../../../types";
import { BlockFormHeader } from "../BlockFormHeader";
import { TextField } from "@/shared/components/Form/TextField";
import { AutocompleteField } from "@/shared/components/Form/AutocompleteField";
import { Switch } from "@/shared/components/Form/Switch";
import { ItemsPerRowField } from "../../ItemsPerRowField";

const previewModeValues = ["HORIZONTAL", "VERTICAL"] as const;

// Значения сайтовой ArchiveBlockProps.sort (ArchiveBlock.tsx витрины): те
// же LATEST/OLDEST/UPDATED/MANUAL, что и у CONTENTS.
const sortValues = ["LATEST", "OLDEST", "UPDATED", "MANUAL"] as const;

/**
 * Блок ARCHIVE: каталог всего видео платформы (легаси-аналог CONTENTS без
 * фильтра по коллекции/тегам). Названия свойств здесь дословно совпадают с
 * тем, что уже лежит в перенесённых легаси-страницах (см. миграции
 * backend/src/migrations/data/*Import/pages.ts): `title`, `filterTagTypes`
 * (`tagTypeId` + `sort` в каждом элементе), `filterTagsBySelectedTags`.
 * Остальные поля (вкладки, закреплённые теги, расширенные фильтры, поиск)
 * в перенесённых страницах не встречаются, их имена свободны, менять уже
 * нечего.
 *
 * Сам компонент витрины (site/.../ArchiveBlock.tsx) пока умеет меньше, чем
 * есть настроек здесь: панель фильтров и вкладки ещё не отрисовываются,
 * пропсы принимаются и не теряются. Форма ниже на полный набор настроек.
 */
export const Archive: PlatformBlockComponent = ({
  name,
  platformId,
  queries,
  values,
}) => {
  const { t } = useTranslation();

  const previewModeOptions = previewModeValues.map((value) => ({
    value,
    label: t(`platformBlocks.previewMode.${value}`),
  }));

  const sortOptions = sortValues.map((value) => ({
    value,
    label: t(`platformBlocks.sort.${value}`),
  }));

  const props = values?.props as Record<string, unknown> | undefined;
  const tabsTagTypeId = props?.tabsTagTypeId as string | undefined;

  return (
    <Paper sx={{ p: 2 }}>
      <BlockFormHeader blockType="ARCHIVE" />
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextField
            name={`${name}.title`}
            label={t("platformBlocks.fields.title")}
            type="text"
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <AutocompleteField
            name={`${name}.tabsTagTypeId`}
            label={t("platformBlocks.archive.tabsTagType")}
            helperText={t("platformBlocks.archive.tabsTagTypeHelper")}
            fullWidth
            query={queries?.autocompleteTagTypes}
            queryVariables={platformId ? { filter: {} } : undefined}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <AutocompleteField
            multiple
            name={`${name}.tabsTagIds`}
            label={t("platformBlocks.archive.tabsTagIds")}
            helperText={t("platformBlocks.archive.tabsTagIdsHelper")}
            fullWidth
            query={queries?.autocompleteTags}
            queryVariables={
              platformId
                ? { filter: { platformId, typeId: tabsTagTypeId } }
                : undefined
            }
          />
        </Grid>
        <Grid size={12}>
          <TextField
            name={`${name}.allVideosText`}
            label={t("platformBlocks.archive.allVideosText")}
            helperText={t("platformBlocks.archive.allVideosTextHelper")}
            type="text"
            fullWidth
          />
        </Grid>

        <Grid size={12}>
          <FieldArray name={`${name}.filterTagTypes`}>
            {({ fields }) => (
              <Stack spacing={2}>
                {fields.map((fieldName, index) => (
                  <Stack key={fieldName} direction="row" alignItems="center">
                    <Grid container spacing={2} flexGrow={1}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <AutocompleteField
                          name={`${fieldName}.tagTypeId`}
                          label={t("platformBlocks.archive.filterTagType")}
                          fullWidth
                          query={queries?.autocompleteTagTypes}
                          queryVariables={
                            platformId ? { filter: {} } : undefined
                          }
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          name={`${fieldName}.sort`}
                          label={t("platformBlocks.archive.filterTagTypeOrder")}
                          placeholder="TITLE_ASC"
                          type="text"
                          fullWidth
                        />
                      </Grid>
                    </Grid>
                    <IconButton
                      type="button"
                      onClick={() => {
                        fields.remove(index);
                      }}
                      size="large"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Stack>
                ))}
                <Button
                  type="button"
                  variant="outlined"
                  fullWidth
                  onClick={() => fields.push({})}
                >
                  {t("platformBlocks.archive.addFilterTagType")}
                </Button>
              </Stack>
            )}
          </FieldArray>
        </Grid>
        <Grid size={12}>
          <Switch
            name={`${name}.filterTagsBySelectedTags`}
            label={t("platformBlocks.archive.filterTagsBySelectedTags")}
          />
        </Grid>

        <Grid size={12}>
          <AutocompleteField
            multiple
            name={`${name}.pinnedTagIds`}
            label={t("platformBlocks.archive.pinnedTagIds")}
            helperText={t("platformBlocks.archive.pinnedTagIdsHelper")}
            fullWidth
            query={queries?.autocompleteTags}
            queryVariables={platformId ? { filter: { platformId } } : undefined}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <Switch
            name={`${name}.showAdvancedFilters`}
            label={t("platformBlocks.archive.showAdvancedFilters")}
            helperText={t("platformBlocks.archive.notYetOnSite")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Switch
            name={`${name}.showSearch`}
            label={t("platformBlocks.archive.showSearch")}
            helperText={t("platformBlocks.archive.notYetOnSite")}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 4 }}>
          <AutocompleteField
            name={`${name}.sort`}
            label={t("platformBlocks.fields.sort")}
            fullWidth
            options={sortOptions}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <ItemsPerRowField
            name={`${name}.itemsPerRow`}
            label={t("platformBlocks.fields.itemsPerRow")}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <AutocompleteField
            name={`${name}.previewMode`}
            label={t("platformBlocks.fields.previewMode")}
            fullWidth
            options={previewModeOptions}
          />
        </Grid>
      </Grid>
    </Paper>
  );
};
